import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, DialogControl, FieldControl, Loading } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import CardGridControl from "../../CardGridControl";
import {
  buildResourceOperationQueryParams,
  buildWizardValidationError,
  getDisplayName,
  getId,
  getOperationItem,
  getOperationItems,
  getValue,
  readValue,
  requireSuccessfulServiceResponse
} from "../resourceConfigWizardUtils";
import {
  CREATE_EDI_SCHEMA_OPERATION,
  CREATE_ENTITY_SCHEMA_FROM_DESTINATION_SCHEMA_OPERATION,
  CREATE_ENTITY_SCHEMA_FROM_SOURCE_OPERATION,
  ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA,
  ENTITY_SCHEMA_MODE_EDI,
  ENTITY_SCHEMA_MODE_MANUAL,
  ENTITY_SCHEMA_MODE_SOURCE,
  getEditableDraftSchemaVersionId,
  getEntitySchemaMode,
  getEntitySchemaModeLabel,
  getSchemaIdFromValues,
  getSchemaStepValues
} from "./entitySchemaStepUtils";
import { LinkedStepHeader } from "./WizardStepCommon";

const activeRecord = (item) => getValue(item, "IsDeleted", "isDeleted") !== true && getValue(item, "IsActive", "isActive") !== false;
const notDeletedRecord = (item) => getValue(item, "IsDeleted", "isDeleted") !== true;
const normalizeId = (value) => String(value ?? "").trim().toLowerCase();
const SOURCE_OBJECT_SELECTOR_METADATA = {
  Fields: [
    { FieldName: "EntitySelectorName", DisplayName: "Name", ShowOnCard: true, ShowOnGrid: true, GridSequence: 1 },
    { FieldName: "EntitySelectorObject", DisplayName: "Object", ShowOnCard: true, ShowOnGrid: true, GridSequence: 2 },
    { FieldName: "EntitySelectorSourceSystem", DisplayName: "Source System", ShowOnCard: true, ShowOnGrid: true, GridSequence: 3 },
    { FieldName: "EntitySelectorKey", DisplayName: "Key", ShowOnGrid: true, GridSequence: 4 },
    { FieldName: "EntitySelectorIsActive", DisplayName: "Active", ShowOnGrid: true, GridSequence: 5 }
  ]
};
const SOURCE_OBJECT_FILTER_OPTIONS = [
  { key: "active", label: "Active" },
  { key: "selected", label: "Selected" },
  { key: "both", label: "Both" }
];
const sourceSystemValue = (sourceSystem) => readValue(sourceSystem, "SourceSystemId", "sourceSystemId", "UniqueId", "uniqueId", "_id", "id") || "";
const fieldValue = (nextValue) => {
  if (nextValue && typeof nextValue === "object" && !Array.isArray(nextValue)) {
    return readValue(nextValue, "Value", "value", "Text", "text") ?? "";
  }
  return nextValue ?? "";
};
const option = (text, value = text, extra = {}) => ({ Text: text, Value: value, ...extra });
const fieldMetadata = ({ fieldName, displayName, fieldType, options, description = "", placeHolder = "" }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  Title: displayName,
  FieldType: fieldType,
  Description: description,
  DataJson: options || [],
  PlaceHolder: placeHolder
});
const sourceObjectTitle = (sourceObject) => getDisplayName(sourceObject) || readValue(sourceObject, "SystemName", "systemName") || getId(sourceObject);
const sourceObjectSubtitle = (sourceObject) => readValue(sourceObject, "InternalName", "internalName", "ObjectName", "objectName") || readValue(sourceObject, "SystemName", "systemName") || "";
const sourceObjectRecordId = (sourceObject) => readValue(sourceObject, "SourceObjectId", "sourceObjectId", "UniqueId", "uniqueId", "_id", "id") || "";
const sourceObjectSourceSystemId = (sourceObject) => fieldValue(readValue(sourceObject, "SourceSystemId", "sourceSystemId")) || "";
const activeFlag = (item) => getValue(item, "IsActive", "isActive") !== false;
const parseSelectedSourceObjectIds = (value) =>
  String(value || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
const normalizeMultiSelectValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (entry && typeof entry === "object") return readValue(entry, "Value", "value", "Text", "text") || "";
        return entry;
      })
      .map((entry) => String(entry || "").trim())
      .filter(Boolean);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return normalizeMultiSelectValue(parsed);
      } catch {
        return [];
      }
    }
    return trimmed.split(",").map((entry) => entry.trim()).filter(Boolean);
  }
  return value === null || value === undefined || value === "" ? [] : [String(value).trim()].filter(Boolean);
};
const sourceObjectCardRow = (sourceObject, sourceSystemLookup = new Map()) => ({
  ...sourceObject,
  EntitySelectorName: sourceObjectTitle(sourceObject),
  EntitySelectorObject: sourceObjectSubtitle(sourceObject) || getId(sourceObject),
  EntitySelectorSourceSystem: sourceSystemLookup.get(normalizeId(sourceObjectSourceSystemId(sourceObject))) || "Source System",
  EntitySelectorKey: getId(sourceObject),
  EntitySelectorIsActive: activeFlag(sourceObject)
});
const filterLeaf = (name, method, value) => ({
  Variable: "x",
  Name: name,
  Method: method,
  Value: value,
  IsConjunction: false
});
const filterConjunction = (method, left, right) => ({
  Variable: "x",
  Method: method,
  Left: left,
  Right: right,
  IsConjunction: true
});
const buildOrFilter = (nodes = []) => nodes.reduce((current, node) => (
  current ? filterConjunction("||", current, node) : node
), null);
const buildSourceObjectQueryModel = (sourceSystemIds = []) => {
  const sourceSystemFilter = buildOrFilter(sourceSystemIds.map((sourceSystemId) =>
    filterLeaf("SourceSystemId", "==", sourceSystemId)
  ));
  const activeSourceObjectFilter = filterConjunction("&&", filterLeaf("IsDeleted", "==", false), filterLeaf("IsActive", "==", true));
  return {
    RootFilterNode: sourceSystemFilter
      ? filterConjunction("&&", activeSourceObjectFilter, sourceSystemFilter)
      : activeSourceObjectFilter
  };
};
const buildEdiGeneratedEntityCandidates = (schemaStepValues = {}) => {
  const transactionSets = normalizeMultiSelectValue(readValue(schemaStepValues, "EdiTransactionSets", "ediTransactionSets", "ts"));
  if (!transactionSets.length) return [];

  const shape = String(readValue(schemaStepValues, "EdiSchemaShape", "ediSchemaShape", "sm") || "All")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
  const candidates = [{ key: "x12_header", label: "X12 Interchange Header (ISA)" }];

  transactionSets.forEach((transactionSet) => {
    if (shape === "flat") {
      candidates.push({ key: `x12_${transactionSet}_flat`, label: `X12 ${transactionSet} Flat` });
      return;
    }
    if (shape === "header") {
      candidates.push({ key: `x12_${transactionSet}_header`, label: `X12 ${transactionSet} Header` });
      return;
    }
    if (shape === "line") {
      candidates.push({ key: `x12_${transactionSet}_line`, label: `X12 ${transactionSet} Line` });
      return;
    }
    if (shape === "combined" || shape === "nested") {
      candidates.push({ key: `x12_${transactionSet}`, label: `X12 ${transactionSet} Combined` });
      return;
    }
    candidates.push(
      { key: `x12_${transactionSet}_flat`, label: `X12 ${transactionSet} Flat` },
      { key: `x12_${transactionSet}_header`, label: `X12 ${transactionSet} Header` },
      { key: `x12_${transactionSet}_line`, label: `X12 ${transactionSet} Line` }
    );
  });

  return candidates;
};

const parseDestinationSchemaSelection = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const buildDestinationSchemaGeneratedEntityCandidates = (schemaStepValues = {}) => {
  const selections = normalizeMultiSelectValue(readValue(
    schemaStepValues,
    "DestinationSchemaSelections",
    "destinationSchemaSelections",
    "SelectedDestinationSchemas",
    "selectedDestinationSchemas",
    "dss"
  ));
  return selections
    .map(parseDestinationSchemaSelection)
    .filter(Boolean)
    .map((selection) => {
      const externalSystemName = readValue(selection, "externalSystemName", "ExternalSystemName") || "";
      const deploymentName = readValue(selection, "deploymentName", "DeploymentName") || "";
      const entityObjectName = readValue(selection, "entityObjectDisplayName", "EntityObjectDisplayName", "entityObjectName", "EntityObjectName") || "Destination Entity Object";
      const labelPrefix = [externalSystemName, deploymentName].filter(Boolean).join(" ");
      return {
        key: readValue(selection, "key", "Key")
          || `destination_${readValue(selection, "externalSystemId", "ExternalSystemId")}_${readValue(selection, "deploymentId", "DeploymentId")}_${readValue(selection, "entityObjectId", "EntityObjectId")}`,
        label: labelPrefix ? `${labelPrefix} - ${entityObjectName}` : entityObjectName
      };
    });
};
const loadSchemaById = async (schemaMgr, schemaId, coreState) => {
  if (!schemaId) return null;
  if (schemaMgr?.getItem) {
    const result = await schemaMgr.getItem(schemaId);
    requireSuccessfulServiceResponse(result, "Entity Schema could not be loaded.", coreState);
    return getOperationItem(result) || null;
  }
  if (schemaMgr?.Query) {
    const result = await schemaMgr.Query({
      model: "x => x.IsDeleted == false && (x._id == schemaId || x.SchemaId == schemaId || x.UniqueId == schemaId)",
      params: { schemaId },
      id: schemaId
    });
    requireSuccessfulServiceResponse(result, "Entity Schema could not be loaded.", coreState);
    return getOperationItems(result).find((item) =>
      getId(item) === schemaId
      || readValue(item, "SchemaId", "schemaId", "UniqueId", "uniqueId") === schemaId
    ) || null;
  }
  return null;
};

const EntitySourceObjectSelectorStep = ({ context, onChange, onSave, onContinue, registerStepController }) => {
  const { wizard, values = {}, readOnly, saving } = context;
  const resourceDomainContext = useResourceDomainContext();
  const coreState = resourceDomainContext?.coreState;
  const sourceSystemMgr = resourceDomainContext?.managers?.sourceSystem;
  const sourceObjectMgr = resourceDomainContext?.managers?.sourceObject;
  const schemaMgr = resourceDomainContext?.managers?.schema;
  const entityObjectMgr = resourceDomainContext?.managers?.entityObject;
  const mode = getEntitySchemaMode(wizard, values, { preferSavedSetupMode: true });
  const schemaStepValues = getSchemaStepValues(wizard, values);
  const schemaId = getSchemaIdFromValues(wizard, schemaStepValues);
  const selectedIdsFromValues = readValue(values, "SelectedSourceObjectIds", "selectedSourceObjectIds") || "";
  const sourceSystemIdFromValues = readValue(values, "SourceSystemId", "sourceSystemId") || "";
  const isSourceMode = mode === ENTITY_SCHEMA_MODE_SOURCE;
  const isManualMode = mode === ENTITY_SCHEMA_MODE_MANUAL;
  const isEdiMode = mode === ENTITY_SCHEMA_MODE_EDI;
  const isDestinationSchemaMode = mode === ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA;
  const supportsSourceObjectSelection = true;
  const [sourceSystems, setSourceSystems] = useState([]);
  const [sourceObjects, setSourceObjects] = useState([]);
  const [sourceSystemId, setSourceSystemId] = useState(sourceSystemIdFromValues);
  const [selectedIds, setSelectedIds] = useState(() => new Set(parseSelectedSourceObjectIds(selectedIdsFromValues)));
  const [loading, setLoading] = useState(supportsSourceObjectSelection);
  const [sourceSystemsLoaded, setSourceSystemsLoaded] = useState(!supportsSourceObjectSelection);
  const [sourceObjectsLoadedKey, setSourceObjectsLoadedKey] = useState("");
  const [localError, setLocalError] = useState("");
  const [ediGenerationConfirm, setEdiGenerationConfirm] = useState(null);
  const [ediSourceObjectMap, setEdiSourceObjectMap] = useState({});
  const selectedIdsRef = useRef(selectedIds);
  const ediSourceObjectMapRef = useRef(ediSourceObjectMap);
  const onChangeRef = useRef(onChange);
  const sourceSystemMgrRef = useRef(sourceSystemMgr);
  const sourceObjectMgrRef = useRef(sourceObjectMgr);
  const modeRef = useRef(mode);
  const schemaIdRef = useRef(schemaId);
  const schemaMgrRef = useRef(schemaMgr);
  const entityObjectMgrRef = useRef(entityObjectMgr);
  const schemaStepValuesRef = useRef(schemaStepValues);
  const sourceSystemIdRef = useRef(sourceSystemId);
  const wizardRef = useRef(wizard);
  const coreStateRef = useRef(coreState);
  const sourceSystemsLoadKeyRef = useRef("");
  const sourceObjectsLoadKeyRef = useRef("");
  const generatedSelectionLoadKeyRef = useRef("");
  const generationPromiseRef = useRef(null);
  const selectedIdsFromValuesRef = useRef(String(selectedIdsFromValues || ""));
  selectedIdsRef.current = selectedIds;
  ediSourceObjectMapRef.current = ediSourceObjectMap;
  onChangeRef.current = onChange;
  sourceSystemMgrRef.current = sourceSystemMgr;
  sourceObjectMgrRef.current = sourceObjectMgr;
  modeRef.current = mode;
  schemaIdRef.current = schemaId;
  schemaMgrRef.current = schemaMgr;
  entityObjectMgrRef.current = entityObjectMgr;
  schemaStepValuesRef.current = schemaStepValues;
  sourceSystemIdRef.current = sourceSystemId;
  wizardRef.current = wizard;
  coreStateRef.current = coreState;
  const sourceSystemMgrReady = Boolean(sourceSystemMgr?.Query);
  const sourceObjectMgrReady = Boolean(sourceObjectMgr?.Query);

  const sourceSystemOptions = useMemo(() => [
    option("All Source Systems", "", { SortOrder: 0, Sequence: 0 }),
    ...sourceSystems.map((sourceSystem) => option(
      getDisplayName(sourceSystem) || readValue(sourceSystem, "SystemName", "systemName") || sourceSystemValue(sourceSystem),
      sourceSystemValue(sourceSystem)
    ))
  ], [sourceSystems]);
  const sourceSystemLookup = useMemo(() => new Map(sourceSystems.map((sourceSystem) => [
    normalizeId(sourceSystemValue(sourceSystem)),
    getDisplayName(sourceSystem) || readValue(sourceSystem, "SystemName", "systemName") || sourceSystemValue(sourceSystem)
  ])), [sourceSystems]);
  const activeSourceSystemIdList = useMemo(
    () => sourceSystems.map(sourceSystemValue).filter(Boolean),
    [sourceSystems]
  );
  const activeSourceSystemIds = useMemo(
    () => new Set(activeSourceSystemIdList.map(normalizeId)),
    [activeSourceSystemIdList]
  );
  const activeSourceSystemIdsKey = useMemo(
    () => activeSourceSystemIdList.slice().sort((left, right) => left.localeCompare(right)).join("|"),
    [activeSourceSystemIdList]
  );
  const expectedSourceObjectsLoadKey = useMemo(() => {
    if (!supportsSourceObjectSelection || !sourceSystemsLoaded || !sourceObjectMgrReady || !activeSourceSystemIdList.length) return "";
    return `all:${activeSourceSystemIdsKey}`;
  }, [activeSourceSystemIdList.length, activeSourceSystemIdsKey, sourceObjectMgrReady, sourceSystemsLoaded, supportsSourceObjectSelection]);
  const isLoading = loading
    || (supportsSourceObjectSelection && sourceSystemMgrReady && !sourceSystemsLoaded)
    || Boolean(expectedSourceObjectsLoadKey && sourceObjectsLoadedKey !== expectedSourceObjectsLoadKey);

  const sourceObjectRows = useMemo(
    () => sourceObjects
      .filter(activeRecord)
      .filter((sourceObject) =>
        !sourceSystemId
        || normalizeId(sourceObjectSourceSystemId(sourceObject)) === normalizeId(sourceSystemId)
      )
      .map((sourceObject) => sourceObjectCardRow(sourceObject, sourceSystemLookup)),
    [sourceObjects, sourceSystemId, sourceSystemLookup]
  );
  const selectedSourceObjects = useMemo(
    () => sourceObjects.filter((sourceObject) =>
      selectedIds.has(getId(sourceObject))
      || selectedIds.has(sourceObjectRecordId(sourceObject))
    ),
    [selectedIds, sourceObjects]
  );
  const generatedEntityCandidates = useMemo(
    () => mode === ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA
      ? buildDestinationSchemaGeneratedEntityCandidates(schemaStepValues)
      : buildEdiGeneratedEntityCandidates(schemaStepValues),
    [mode, schemaStepValues]
  );
  const ediSourceObjectOptions = useMemo(() => [
    option("No Source Object of Record", ""),
    ...selectedSourceObjects.map((sourceObject) => option(
      sourceObjectTitle(sourceObject),
      sourceObjectRecordId(sourceObject),
      { Description: sourceObjectSubtitle(sourceObject) }
    ))
  ], [selectedSourceObjects]);
  const selectedCount = selectedIds.size;
  const filterSourceObjectRows = useCallback((row, { mode: filterMode, isActive, isSelected }) => {
    if (!isActive && !isSelected) return false;
    if (filterMode === "selected") return Boolean(isSelected);
    if (filterMode === "both") return Boolean(isActive || isSelected);
    return Boolean(isActive);
  }, []);

  useEffect(() => {
    const nextSourceSystemId = String(sourceSystemIdFromValues || "");
    if (nextSourceSystemId === sourceSystemIdRef.current) return;
    generatedSelectionLoadKeyRef.current = "";
    setSourceSystemId(nextSourceSystemId);
  }, [sourceSystemIdFromValues]);

  useEffect(() => {
    const selectedIdsKey = String(selectedIdsFromValues || "");
    if (selectedIdsFromValuesRef.current === selectedIdsKey) return;
    selectedIdsFromValuesRef.current = selectedIdsKey;
    generatedSelectionLoadKeyRef.current = "";
    setSelectedIds(new Set(parseSelectedSourceObjectIds(selectedIdsFromValues)));
  }, [selectedIdsFromValues]);

  const loadSourceSystems = useCallback(async () => {
    const activeSourceSystemMgr = sourceSystemMgrRef.current;
    if (!activeSourceSystemMgr?.Query) throw new Error("Source System service is not available.");
    const result = await activeSourceSystemMgr.Query({ model: "x => x.IsDeleted == false && x.IsActive == true" });
    requireSuccessfulServiceResponse(result, "Source Systems could not be loaded.", coreStateRef.current);
    return getOperationItems(result).filter(activeRecord);
  }, []);

  const loadSourceObjects = useCallback(async (nextSourceSystemIds = []) => {
    const activeSourceObjectMgr = sourceObjectMgrRef.current;
    if (!activeSourceObjectMgr?.Query) return [];
    const sourceSystemIds = [...new Set((Array.isArray(nextSourceSystemIds) ? nextSourceSystemIds : [nextSourceSystemIds])
      .map((entry) => String(entry || "").trim())
      .filter(Boolean))];
    const result = await activeSourceObjectMgr.Query({
      model: buildSourceObjectQueryModel(sourceSystemIds),
      params: { sourceSystemIds }
    });
    requireSuccessfulServiceResponse(result, "Source Objects could not be loaded.", coreStateRef.current);
    return getOperationItems(result)
      .filter(activeRecord);
  }, []);

  const loadGeneratedEntityObjects = useCallback(async () => {
    const currentSchemaId = schemaIdRef.current;
    const currentSchemaMgr = schemaMgrRef.current;
    const currentEntityObjectMgr = entityObjectMgrRef.current;
    if (!currentSchemaId || !currentSchemaMgr || !currentEntityObjectMgr?.Query) return [];

    const schema = await loadSchemaById(currentSchemaMgr, currentSchemaId, coreStateRef.current);
    const schemaVersionId = getEditableDraftSchemaVersionId(schema);
    if (!schemaVersionId) return [];

    const result = await currentEntityObjectMgr.Query({
      model: "x => x.IsDeleted == false && x.SchemaVersionId == schemaVersionId",
      params: { schemaVersionId }
    });
    requireSuccessfulServiceResponse(result, "Entity Objects could not be loaded.", coreStateRef.current);
    return getOperationItems(result).filter(activeRecord);
  }, []);

  useEffect(() => {
    if (!supportsSourceObjectSelection) {
      setLoading(false);
      setSourceSystemsLoaded(true);
      setSourceObjectsLoadedKey("");
      sourceSystemsLoadKeyRef.current = "";
      return undefined;
    }
    if (!sourceSystemMgrReady) {
      setLoading(false);
      setSourceSystemsLoaded(true);
      setSourceObjectsLoadedKey("");
      setLocalError("Source System service is not available.");
      return undefined;
    }
    const loadKey = sourceSystemMgrReady ? "entity-schema-source-systems" : "";
    if (!loadKey || sourceSystemsLoadKeyRef.current === loadKey) return undefined;
    sourceSystemsLoadKeyRef.current = loadKey;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setSourceSystemsLoaded(false);
      setSourceObjectsLoadedKey("");
      setLocalError("");
      try {
        const systems = await loadSourceSystems();
        if (!mounted) return;
        setSourceSystems(systems);
        setSourceSystemsLoaded(true);
        if (!systems.length || !sourceObjectMgrReady) setLoading(false);
      } catch (ex) {
        if (mounted) {
          setSourceSystemsLoaded(true);
          setLoading(false);
          setLocalError(ex?.message || "Source Object selection could not be loaded.");
        }
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [loadSourceSystems, sourceObjectMgrReady, sourceSystemMgrReady, supportsSourceObjectSelection]);

  useEffect(() => {
    if (!supportsSourceObjectSelection) {
      sourceObjectsLoadKeyRef.current = "";
      setSourceObjects((previous) => previous.length ? [] : previous);
      setSourceObjectsLoadedKey("");
      return undefined;
    }
    if (!sourceSystemsLoaded) {
      setLoading(true);
      return undefined;
    }
    if (!sourceObjectMgrReady) {
      setLoading(false);
      setSourceObjects((previous) => previous.length ? [] : previous);
      setSourceObjectsLoadedKey("");
      setLocalError("Source Object service is not available.");
      return undefined;
    }
    if (!sourceSystems.length) {
      sourceObjectsLoadKeyRef.current = "";
      setSourceObjects((previous) => previous.length ? [] : previous);
      setSourceObjectsLoadedKey("");
      setLoading(false);
      return undefined;
    }
    if (!activeSourceSystemIdList.length) {
      sourceObjectsLoadKeyRef.current = "";
      setSourceObjects((previous) => previous.length ? [] : previous);
      setSourceObjectsLoadedKey("");
      setLoading(false);
      return undefined;
    }
    const loadKey = expectedSourceObjectsLoadKey;
    if (sourceObjectsLoadKeyRef.current === loadKey) return undefined;
    sourceObjectsLoadKeyRef.current = loadKey;
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setLocalError("");
      try {
        const objects = await loadSourceObjects(activeSourceSystemIdList);
        const filteredObjects = objects.filter((sourceObject) => activeSourceSystemIds.has(normalizeId(sourceObjectSourceSystemId(sourceObject))));
        if (mounted) {
          setSourceObjects(filteredObjects);
          setSourceObjectsLoadedKey(loadKey);
        }
      } catch (ex) {
        if (mounted) {
          setSourceObjectsLoadedKey(loadKey);
          setLocalError(ex?.message || "Source Objects could not be loaded.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [activeSourceSystemIdList, activeSourceSystemIds, expectedSourceObjectsLoadKey, loadSourceObjects, sourceObjectMgrReady, sourceSystems.length, sourceSystemsLoaded, supportsSourceObjectSelection]);

  useEffect(() => {
    if (mode !== ENTITY_SCHEMA_MODE_SOURCE) return undefined;
    if (String(selectedIdsFromValues || "").trim()) return undefined;
    if (selectedIdsRef.current.size > 0) return undefined;
    if (!schemaId || !sourceObjects.length) return undefined;

    const sourceObjectKeys = sourceObjects.map((sourceObject) => getId(sourceObject)).join("|");
    const loadKey = `${schemaId}::${sourceSystemId}::${sourceObjectKeys}`;
    if (generatedSelectionLoadKeyRef.current === loadKey) return undefined;
    generatedSelectionLoadKeyRef.current = loadKey;

    let mounted = true;
    const load = async () => {
      try {
        const entityObjects = await loadGeneratedEntityObjects();
        if (!mounted || selectedIdsRef.current.size > 0) return;
        const generatedSourceObjectIds = new Set(
          entityObjects
            .map((entityObject) => normalizeId(readValue(entityObject, "SourceObjectOfRecordId", "sourceObjectOfRecordId")))
            .filter(Boolean)
        );
        if (!generatedSourceObjectIds.size) return;
        const nextSelectedIds = sourceObjects
          .filter((sourceObject) => generatedSourceObjectIds.has(normalizeId(sourceObjectRecordId(sourceObject))))
          .map(getId)
          .filter(Boolean);
        if (nextSelectedIds.length) {
          setSelectedIds(new Set(nextSelectedIds));
        }
      } catch (ex) {
        if (mounted) setLocalError(ex?.message || "Selected Source Objects could not be resolved from Entity Objects.");
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [loadGeneratedEntityObjects, mode, schemaId, selectedIdsFromValues, sourceObjects, sourceSystemId]);

  const updateSelectedSourceObjectKeys = (nextKeys) => {
    const normalizedKeys = (Array.isArray(nextKeys) ? nextKeys : []).map((key) => String(key || "").trim()).filter(Boolean);
    setSelectedIds(new Set(normalizedKeys));
    onChange?.("SelectedSourceObjectIds", normalizedKeys.join(","));
  };

  const updateSourceSystem = (nextSourceSystemId) => {
    setSourceSystemId(nextSourceSystemId);
  };

  const buildDefaultEdiSourceObjectMap = useCallback((existingMap = {}) => {
    const nextMap = {};
    generatedEntityCandidates.forEach((candidate, index) => {
      const existingValue = existingMap[candidate.key];
      const fallbackSourceObject = selectedSourceObjects[index] || selectedSourceObjects[0];
      nextMap[candidate.key] = existingValue !== undefined
        ? existingValue
        : fallbackSourceObject
          ? sourceObjectRecordId(fallbackSourceObject)
          : "";
    });
    return nextMap;
  }, [generatedEntityCandidates, selectedSourceObjects]);

  const confirmEdiGeneration = useCallback(() => {
    if (modeRef.current !== ENTITY_SCHEMA_MODE_EDI && modeRef.current !== ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA) return true;
    setEdiSourceObjectMap((previous) => buildDefaultEdiSourceObjectMap(previous));
    return new Promise((resolve) => {
      setEdiGenerationConfirm({ resolve });
    });
  }, [buildDefaultEdiSourceObjectMap]);

  const closeEdiGenerationConfirm = useCallback((confirmed) => {
    const resolver = ediGenerationConfirm?.resolve;
    setEdiGenerationConfirm(null);
    resolver?.(confirmed);
  }, [ediGenerationConfirm]);

  const updateEdiSourceObjectMap = (generatedEntityKey, sourceObjectId) => {
    setEdiSourceObjectMap((previous) => ({
      ...previous,
      [generatedEntityKey]: sourceObjectId || ""
    }));
  };

  const runEdiGeneration = useCallback(async (currentValues, selectedSourceObjectIds, selectedSourceObjectIdText) => {
    const currentSchemaId = schemaIdRef.current;
    const currentSchemaMgr = schemaMgrRef.current;
    const currentSchemaStepValues = schemaStepValuesRef.current;
    const currentWizard = wizardRef.current;
    if (!currentSchemaId) throw buildWizardValidationError("Create the Entity Schema before generating EDI Entity Objects.");
    if (!currentSchemaMgr?.RunOperation) throw new Error("Schema operation service is not available.");

    const transactionSets = normalizeMultiSelectValue(readValue(currentSchemaStepValues, "EdiTransactionSets", "ediTransactionSets", "ts"));
    if (!transactionSets.length) throw buildWizardValidationError("Select at least one EDI Transaction Set.");

    const sourceObjectOfRecordMap = buildDefaultEdiSourceObjectMap(ediSourceObjectMapRef.current);
    const generationKey = [
      "edi",
      currentSchemaId,
      transactionSets.join("|"),
      readValue(currentSchemaStepValues, "EdiFormat", "ediFormat", "ediformat") || "X12",
      readValue(currentSchemaStepValues, "EdiDirection", "ediDirection", "edidirection") || "Outbound",
      readValue(currentSchemaStepValues, "EdiSchemaShape", "ediSchemaShape", "sm") || "All",
      JSON.stringify(sourceObjectOfRecordMap),
      readValue(currentSchemaStepValues, "CreateNewSchemaVersion", "createNewSchemaVersion", "nsv") === true
    ].join("::");
    if (generationPromiseRef.current?.key === generationKey) {
      return generationPromiseRef.current.promise;
    }

    const generationPromise = (async () => {
      const result = await currentSchemaMgr.RunOperation({
        operationName: CREATE_EDI_SCHEMA_OPERATION,
        item: { _id: currentSchemaId },
        queryParams: buildResourceOperationQueryParams({
          wizard: currentWizard,
          queryParams: {
            id: currentSchemaId,
            ediformat: readValue(currentSchemaStepValues, "EdiFormat", "ediFormat", "ediformat") || "X12",
            edidirection: readValue(currentSchemaStepValues, "EdiDirection", "ediDirection", "edidirection") || "Outbound",
            ts: transactionSets,
            sm: readValue(currentSchemaStepValues, "EdiSchemaShape", "ediSchemaShape", "sm") || "All",
            nsv: readValue(currentSchemaStepValues, "CreateNewSchemaVersion", "createNewSchemaVersion", "nsv") === true,
            name: readValue(currentSchemaStepValues, "SchemaName", "schemaName", "name"),
            desc: readValue(currentSchemaStepValues, "SchemaDescription", "schemaDescription", "desc"),
            sourceObjectOfRecordMap: JSON.stringify(sourceObjectOfRecordMap)
          }
        })
      });
      requireSuccessfulServiceResponse(result, "EDI Entity Objects could not be generated.", coreStateRef.current);
      const schema = getOperationItem(result);
      return {
        ...currentValues,
        SourceSystemId: "",
        SelectedSourceObjectIds: selectedSourceObjectIdText,
        SelectedSourceObjectCount: selectedSourceObjectIds.length,
        SchemaId: getId(schema) || currentSchemaId
      };
    })();
    generationPromiseRef.current = { key: generationKey, promise: generationPromise };
    try {
      return await generationPromise;
    } finally {
      if (generationPromiseRef.current?.key === generationKey) {
        generationPromiseRef.current = null;
      }
    }
  }, [buildDefaultEdiSourceObjectMap]);

  const runDestinationSchemaGeneration = useCallback(async (currentValues, selectedSourceObjectIds, selectedSourceObjectIdText) => {
    const currentSchemaId = schemaIdRef.current;
    const currentSchemaMgr = schemaMgrRef.current;
    const currentSchemaStepValues = schemaStepValuesRef.current;
    const currentWizard = wizardRef.current;
    if (!currentSchemaId) throw buildWizardValidationError("Create the Entity Schema before generating from Destination Schemas.");
    if (!currentSchemaMgr?.RunOperation) throw new Error("Schema operation service is not available.");

    const destinationSelections = normalizeMultiSelectValue(readValue(
      currentSchemaStepValues,
      "DestinationSchemaSelections",
      "destinationSchemaSelections",
      "SelectedDestinationSchemas",
      "selectedDestinationSchemas",
      "dss"
    ));
    if (!destinationSelections.length) throw buildWizardValidationError("Select at least one Destination Schema.");

    const sourceObjectOfRecordMap = buildDefaultEdiSourceObjectMap(ediSourceObjectMapRef.current);
    const generationKey = [
      "destination-schema",
      currentSchemaId,
      destinationSelections.join("|"),
      JSON.stringify(sourceObjectOfRecordMap),
      readValue(currentSchemaStepValues, "CreateNewSchemaVersion", "createNewSchemaVersion", "nsv") === true
    ].join("::");
    if (generationPromiseRef.current?.key === generationKey) {
      return generationPromiseRef.current.promise;
    }

    const generationPromise = (async () => {
      const result = await currentSchemaMgr.RunOperation({
        operationName: CREATE_ENTITY_SCHEMA_FROM_DESTINATION_SCHEMA_OPERATION,
        item: { _id: currentSchemaId },
        queryParams: buildResourceOperationQueryParams({
          wizard: currentWizard,
          queryParams: {
            id: currentSchemaId,
            selectedDestinationSchemas: destinationSelections,
            name: readValue(currentSchemaStepValues, "SchemaName", "schemaName", "name"),
            desc: readValue(currentSchemaStepValues, "SchemaDescription", "schemaDescription", "desc"),
            nsv: readValue(currentSchemaStepValues, "CreateNewSchemaVersion", "createNewSchemaVersion", "nsv") === true,
            sourceObjectOfRecordMap: JSON.stringify(sourceObjectOfRecordMap)
          }
        })
      });
      requireSuccessfulServiceResponse(result, "Entity Objects could not be generated from selected Destination Schemas.", coreStateRef.current);
      const schema = getOperationItem(result);
      return {
        ...currentValues,
        SourceSystemId: "",
        SelectedSourceObjectIds: selectedSourceObjectIdText,
        SelectedSourceObjectCount: selectedSourceObjectIds.length,
        DestinationSchemaSelections: destinationSelections,
        SchemaId: getId(schema) || currentSchemaId
      };
    })();
    generationPromiseRef.current = { key: generationKey, promise: generationPromise };
    try {
      return await generationPromise;
    } finally {
      if (generationPromiseRef.current?.key === generationKey) {
        generationPromiseRef.current = null;
      }
    }
  }, [buildDefaultEdiSourceObjectMap]);

  const prepareValues = useCallback(async (currentValues = {}) => {
    const currentMode = modeRef.current;
    const currentSchemaId = schemaIdRef.current;
    const currentSchemaMgr = schemaMgrRef.current;
    const currentSchemaStepValues = schemaStepValuesRef.current;
    const currentWizard = wizardRef.current;
    const selectedSourceObjectIds = Array.from(selectedIdsRef.current);
    const selectedSourceObjectIdText = selectedSourceObjectIds.join(",");
    if (currentMode === ENTITY_SCHEMA_MODE_EDI) {
      return runEdiGeneration(currentValues, selectedSourceObjectIds, selectedSourceObjectIdText);
    }
    if (currentMode === ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA) {
      return runDestinationSchemaGeneration(currentValues, selectedSourceObjectIds, selectedSourceObjectIdText);
    }
    if (currentMode !== ENTITY_SCHEMA_MODE_SOURCE && !(currentMode === ENTITY_SCHEMA_MODE_MANUAL && selectedSourceObjectIds.length > 0)) {
      return {
        ...(currentValues || {}),
        SourceSystemId: "",
        SelectedSourceObjectIds: selectedSourceObjectIdText,
        SelectedSourceObjectCount: selectedSourceObjectIds.length,
        SchemaId: currentSchemaId || readValue(currentValues, "SchemaId", "schemaId")
      };
    }
    if (!currentSchemaId) throw buildWizardValidationError("Create the Entity Schema before selecting Source Objects.");
    if (!selectedSourceObjectIds.length) throw buildWizardValidationError("Select at least one Source Object.");
    if (!currentSchemaMgr?.RunOperation) throw new Error("Schema operation service is not available.");
    const generateFields = currentMode === ENTITY_SCHEMA_MODE_SOURCE;

    const generationKey = [
      "source",
      currentMode,
      currentSchemaId,
      selectedSourceObjectIds.slice().sort((left, right) => left.localeCompare(right)).join("|"),
      generateFields,
      readValue(currentSchemaStepValues, "CreateNewSchemaVersion", "createNewSchemaVersion", "nsv") === true
    ].join("::");
    if (generationPromiseRef.current?.key === generationKey) {
      return generationPromiseRef.current.promise;
    }

    const generationPromise = (async () => {
      const result = await currentSchemaMgr.RunOperation({
        operationName: CREATE_ENTITY_SCHEMA_FROM_SOURCE_OPERATION,
        item: { _id: currentSchemaId },
        queryParams: buildResourceOperationQueryParams({
          wizard: currentWizard,
          queryParams: {
            id: currentSchemaId,
            sourceSystemId: "",
            selectedSourceObjectIds: selectedSourceObjectIdText,
            generateFields,
            name: readValue(currentSchemaStepValues, "SchemaName", "schemaName", "name"),
            desc: readValue(currentSchemaStepValues, "SchemaDescription", "schemaDescription", "desc"),
            nsv: readValue(currentSchemaStepValues, "CreateNewSchemaVersion", "createNewSchemaVersion", "nsv") === true
          }
        })
      });
      requireSuccessfulServiceResponse(result, "Entity Objects could not be generated from selected Source Objects.", coreStateRef.current);
      const schema = getOperationItem(result);
      return {
        ...currentValues,
        SourceSystemId: "",
        SelectedSourceObjectIds: selectedSourceObjectIdText,
        SelectedSourceObjectCount: selectedSourceObjectIds.length,
        SchemaId: getId(schema) || currentSchemaId
      };
    })();
    generationPromiseRef.current = { key: generationKey, promise: generationPromise };
    try {
      return await generationPromise;
    } finally {
      if (generationPromiseRef.current?.key === generationKey) {
        generationPromiseRef.current = null;
      }
    }
  }, [runDestinationSchemaGeneration, runEdiGeneration]);

  useEffect(() => {
    registerStepController?.({
      confirmContinue: confirmEdiGeneration,
      prepareSave: async (currentValues = {}) => {
        const selectedSourceObjectIds = Array.from(selectedIdsRef.current);
        const selectedSourceObjectIdText = selectedSourceObjectIds.join(",");
        if (modeRef.current !== ENTITY_SCHEMA_MODE_SOURCE) {
          return {
            ...(currentValues || {}),
            SourceSystemId: "",
            SelectedSourceObjectIds: selectedSourceObjectIdText,
            SelectedSourceObjectCount: selectedSourceObjectIds.length,
            SchemaId: schemaIdRef.current || readValue(currentValues, "SchemaId", "schemaId")
          };
        }
        return {
          ...currentValues,
          SourceSystemId: "",
          SelectedSourceObjectIds: selectedSourceObjectIdText,
          SelectedSourceObjectCount: selectedSourceObjectIds.length,
          SchemaId: schemaIdRef.current
        };
      },
      prepareContinue: prepareValues
    });
    return () => registerStepController?.(null);
  }, [confirmEdiGeneration, prepareValues, registerStepController]);

  const stepDescription = isSourceMode
    ? "Select Source Objects from active Source Systems. Selected Source Objects generate Entity Objects and fields for this schema."
    : isManualMode
      ? "Optionally select Source Objects from active Source Systems. Selected Source Objects create matching Entity Objects without generating fields; you can skip this step and add Entity Objects manually."
      : isDestinationSchemaMode
        ? "Optionally select Source Objects to assign Source Object of Record links to generated Destination Schema Entity Objects. Continue opens an assignment dialog before Entity Objects and fields are generated."
        : "Optionally select Source Objects to assign Source Object of Record links to generated EDI Entity Objects. Continue opens a mapping dialog before EDI Entity Objects are generated.";
  const modeLabel = getEntitySchemaModeLabel(wizard, mode);
  const showGenerateContinue = isSourceMode || isEdiMode || isDestinationSchemaMode || (isManualMode && selectedCount > 0);

  return (
    <div className="resource-wizard__linked-step">
      <LinkedStepHeader title={`Source Objects: ${modeLabel}`} description={stepDescription} countText={`${selectedCount} selected`} />
      {localError ? <div className="resource-wizard__inline-error">{localError}</div> : null}
      <div className="resource-wizard__linked-record-catalog resource-wizard__linked-record-catalog--compact">
        <div className="resource-wizard__linked-record-catalog-scroll">
          {isLoading ? (
            <div className="resource-wizard__linked-step-loading"><Loading /></div>
          ) : (
            <CardGridControl
              rows={sourceObjectRows}
              metadata={SOURCE_OBJECT_SELECTOR_METADATA}
              cardFields={SOURCE_OBJECT_SELECTOR_METADATA.Fields.filter((field) => field.ShowOnCard)}
              rowKey={getId}
              selectedRowKeys={Array.from(selectedIds)}
              onSelectedRowKeysChange={updateSelectedSourceObjectKeys}
              selectable={true}
              multiSelect={true}
              readOnly={readOnly}
              disabled={saving || isLoading}
              activeFilterFieldName="EntitySelectorIsActive"
              activeFilterOptions={SOURCE_OBJECT_FILTER_OPTIONS}
              activeFilterRowPredicate={filterSourceObjectRows}
              toolbarLeadingContent={(
                <FieldControl
                  key={`entity-source-system-${sourceSystemOptions.length}`}
                  className="resource-wizard__field-control resource-wizard__toolbar-filter-control"
                  fieldMetadata={fieldMetadata({
                    fieldName: "SourceSystemId",
                    displayName: "",
                    fieldType: SystemTypes.UIFieldType.SearchSelectList,
                    options: sourceSystemOptions,
                    placeHolder: "All Source Systems"
                  })}
                  selectData={sourceSystemOptions}
                  value={sourceSystemId || ""}
                    disabled={readOnly || saving || isLoading}
                  groupId="entitySourceObjectSelector"
                  onChange={(nextValue) => updateSourceSystem(fieldValue(nextValue))}
                  disableError
                />
              )}
              emptyContent={sourceSystemId ? "No Source Objects are available for this Source System." : "No Source Objects are available across active Source Systems."}
              minCardWidth="15rem"
            />
          )}
        </div>
      </div>
      <div className="resource-wizard__linked-step-actions">
        <div />
        <div className="resource-wizard__linked-step-actions-right">
          <ButtonControl type="standard-small" className="resource-wizard__button resource-wizard__button--secondary" onClick={() => onSave?.()} disabled={readOnly || saving || isLoading} loading={saving}>Save</ButtonControl>
          <ButtonControl type="ok-small" className="resource-wizard__button resource-wizard__button--primary" onClick={() => onContinue?.()} disabled={readOnly || saving || isLoading || (isSourceMode && selectedCount === 0)} loading={saving}>{showGenerateContinue ? "Generate & Continue" : "Save & Continue"}</ButtonControl>
        </div>
      </div>
      <DialogControl
        title={mode === ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA ? "Generate Destination Schema Entity Objects" : "Generate EDI Entity Objects"}
        openDialog={Boolean(ediGenerationConfirm)}
        okText="Generate"
        cancelText="Cancel"
        onOk={() => closeEdiGenerationConfirm(true)}
        onCancel={() => closeEdiGenerationConfirm(false)}
        onX={() => closeEdiGenerationConfirm(false)}
      >
        <p>
          {mode === ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA
            ? "Convoke will generate Entity Objects and fields from the selected External System destination schemas. Assigning Source Object of Record links now ties the generated Entity Objects to source data used for mapping."
            : "Convoke will generate EDI Entity Objects from the selected transaction sets. Assigning Source Object of Record links now keeps the generated EDI objects tied to the source data used for mapping and document generation."}
        </p>
        {!selectedSourceObjects.length ? (
          <p>No Source Objects are selected, so generated Entity Objects will not have Source Object of Record links.</p>
        ) : (
          <div className="resource-wizard__fields">
            {generatedEntityCandidates.map((candidate) => (
              <div className="resource-wizard__field" key={candidate.key}>
                <FieldControl
                  className="resource-wizard__field-control"
                  fieldMetadata={fieldMetadata({
                    fieldName: `SourceObjectOfRecord_${candidate.key}`,
                    displayName: candidate.label,
                    fieldType: SystemTypes.UIFieldType.SearchSelectList,
                    options: ediSourceObjectOptions,
                    placeHolder: "No Source Object of Record"
                  })}
                  selectData={ediSourceObjectOptions}
                  value={ediSourceObjectMap[candidate.key] || ""}
                  disabled={readOnly || saving}
                  groupId="entitySchemaEdiSourceObjectMap"
                  onChange={(nextValue) => updateEdiSourceObjectMap(candidate.key, fieldValue(nextValue))}
                  disableError
                />
              </div>
            ))}
          </div>
        )}
      </DialogControl>
    </div>
  );
};

export default EntitySourceObjectSelectorStep;
